import PropTypes from 'prop-types';
import { BsX } from 'react-icons/bs';

/**
 * The shared surface behind every popup and dropdown: header, body, footer.
 *
 * Panel owns how a floating surface looks; Modal and Popover own where it appears. Change the
 * corporate identity here and every dialog, menu and picker in the app follows.
 */

export const PANEL_SIZES = {
	s: 'max-w-sm',
	m: 'max-w-lg',
	l: 'max-w-2xl',
	// Workspace dialogs: import wizards, pickers and editors that carry their own multi-column
	// layout and would collapse at 'l'.
	xl: 'max-w-[1180px]',
};

const SURFACE = 'bg-white rounded-card border border-bgBase-500 shadow-elevated flex flex-col min-h-0 overflow-hidden';

const PanelHeader = ({ icon: Icon, children, subtitle, onClose, actions }) => (
	<div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-bgBase-500 bg-bgBase-300 flex-shrink-0">
		<div className="flex items-center gap-2.5 min-w-0">
			{Icon && <Icon className="w-5 h-5 text-primary-500 flex-shrink-0" />}
			<div className="min-w-0">
				<h2 className="text-md-bold text-text truncate">{children}</h2>
				{subtitle && <p className="text-xs text-bgBase-800 truncate mt-0.5">{subtitle}</p>}
			</div>
		</div>
		<div className="flex items-center gap-1 flex-shrink-0">
			{actions}
			{onClose && (
				<button
					type="button"
					onClick={onClose}
					aria-label="Schließen"
					className="w-8 h-8 rounded-control text-bgBase-800 hover:text-text hover:bg-bgBase-400 flex items-center justify-center transition-colors"
				>
					<BsX className="w-5 h-5" />
				</button>
			)}
		</div>
	</div>
);

PanelHeader.propTypes = {
	icon: PropTypes.elementType,
	children: PropTypes.node,
	subtitle: PropTypes.node,
	onClose: PropTypes.func,
	actions: PropTypes.node,
};

const PanelBody = ({ children, className = '', padded = true, scroll = true }) => (
	<div
		className={`${padded ? 'px-5 py-4' : ''} ${
			scroll ? 'overflow-y-auto' : 'overflow-hidden'
		} flex-1 min-h-0 ${className}`.trim()}
	>
		{children}
	</div>
);

PanelBody.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	padded: PropTypes.bool,
	// false when the body's own children scroll, e.g. a multi-column workspace layout.
	scroll: PropTypes.bool,
};

const PanelFooter = ({ children, className = '', stacked = false }) => (
	<div
		className={`flex gap-3 px-5 py-3.5 border-t border-bgBase-500 bg-bgBase-300 flex-shrink-0 ${
			stacked ? 'flex-col items-stretch' : 'items-center justify-between'
		} ${className}`.trim()}
	>
		{children}
	</div>
);

PanelFooter.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	stacked: PropTypes.bool,
};

// A dialog that submits renders the surface as the <form> itself, so header, body and footer stay
// direct flex children and the body keeps scrolling independently of the footer's buttons.
const Panel = ({ children, className = '', as: Element = 'div', ...rest }) => (
	<Element className={`${SURFACE} ${className}`.trim()} {...rest}>
		{children}
	</Element>
);

Panel.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	as: PropTypes.elementType,
};

Panel.Header = PanelHeader;
Panel.Body = PanelBody;
Panel.Footer = PanelFooter;

export default Panel;
